import { Code, Description } from '@mui/icons-material';
import {
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { CurveData } from '~/domain/patient/dvh';
import { useDoseStatisticsExport } from '~/hooks/patient/dose/useDoseStatisticsExport';
import { TITLE_BAR_ICON_SX } from '~/viewportGrid';
import { DoseTable } from './useDoseTableSelection';

interface DoseStatisticsExportMenuProps {
    /** The statistics on offer: the curves the table shows. */
    readonly curves: CurveData[];
    /** The table the tile shows; the downloads on offer are that table's. */
    readonly table: DoseTable;
}

/**
 * The export control of a dose statistics tile: one button in the title
 * bar that opens the downloads of the table shown. The statistics offer
 * every structure's DVH as CSV and the statistics as CSV or JSON; the
 * optimization objectives offer themselves as CSV, under the rows that
 * name the dose, or JSON. Each file is named with the date and time of
 * the export.
 */
export default function DoseStatisticsExportMenu({
    curves,
    table,
}: DoseStatisticsExportMenuProps) {
    const { formatMessage: t } = useIntl();
    const {
        exportDvhCsv,
        exportStatisticsCsv,
        exportStatisticsJson,
        exportObjectivesCsv,
        exportObjectivesJson,
    } = useDoseStatisticsExport(curves);
    const showsObjectives = table === 'objectives';
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const close = () => setAnchor(null);
    const run = (action: () => void | Promise<unknown>) => () => {
        void action();
        close();
    };
    return (
        <>
            {/* The title bar runs across the top of the tile, so the hint
                opens down onto the table, as its neighbours' do. */}
            <Tooltip title={t({ id: 'export' })} placement="bottom">
                <IconButton
                    size="small"
                    aria-label={t({ id: 'export' })}
                    data-testid="dose-statistics-export"
                    sx={TITLE_BAR_ICON_SX}
                    onClick={(event) => setAnchor(event.currentTarget)}
                >
                    <Download size={13} />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
                {!showsObjectives && (
                    <MenuItem onClick={run(exportDvhCsv)}>
                        <ListItemIcon>
                            <Description />
                        </ListItemIcon>
                        <ListItemText>
                            {t({ id: 'exportDvhAsCsv' })}
                        </ListItemText>
                    </MenuItem>
                )}
                {!showsObjectives && <Divider />}
                <MenuItem
                    onClick={run(
                        showsObjectives
                            ? exportObjectivesCsv
                            : exportStatisticsCsv
                    )}
                >
                    <ListItemIcon>
                        <Description />
                    </ListItemIcon>
                    <ListItemText>
                        {t({
                            id: showsObjectives
                                ? 'exportOptimizationObjectivesAsCsv'
                                : 'exportDoseStatisticsAsCsv',
                        })}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={run(
                        showsObjectives
                            ? exportObjectivesJson
                            : exportStatisticsJson
                    )}
                >
                    <ListItemIcon>
                        <Code />
                    </ListItemIcon>
                    <ListItemText>
                        {t({
                            id: showsObjectives
                                ? 'exportOptimizationObjectivesAsJson'
                                : 'exportDoseStatisticsAsJson',
                        })}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
